<template>
    <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form id="schema-input-form"
                autocomplete="new-password"
                @submit.stop.prevent="handleSubmit(confirmAndSave)"
                @reset="resetForm"
        >
            <dl class="description-list-wrapper">
                <DescriptionItem :label="$t('연결 프로젝트')" mode="form" labelFor="edit-form-project"
                                 required
                >
                    <div class="d-flex flex-column w-100">
                        <validation-provider v-slot="validationContext" name="edit-form-project" vid="edit-form-project"
                                             :rules="{ required: true }">
                            <div class="d-flex align-items-center rounded text-secondary w-100">
                                <auto-complete-select id="edit-form-project"
                                                      ref="projectCodeAutoComplete"
                                                      v-model="selectedProjectCodeId"
                                                      class="w-100"
                                                      :options="projectCodeOptions"
                                                      start-expanded
                                                      list-max-height="360px"
                                                      option-value-key="id"
                                                      :disabled="mode === 'edit' || lockProjectToSelected"
                                                      :placeholder="$t('프로젝트를 선택해주세요.')"
                                                      :state="getValidationState(validationContext)"
                                                      autocomplete="new-password"
                                                      @click="validationContext.validate()"
                                >
                                </auto-complete-select>
                            </div>
                            <b-form-invalid-feedback id="edit-form-key-name-feedback" class="d-block">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('키 이름')" mode="form" labelFor="edit-form-key-name"
                                 required
                >
                    <div class="w-100">
                        <validation-provider
                            v-slot="validationContext"
                            name="edit-form-key-name"
                            :rules="{ required: true, min: 2 }"
                        >
                            <b-form-input id="edit-form-key-name"
                                          v-model="form.key_name"
                                          name="edit-form-key-name"
                                          :disabled="editLoading || mode === 'edit'"
                                          :placeholder="$t('예: age, city, category')"
                                          :state="getValidationState(validationContext)"
                                          autocomplete="new-password"
                                          aria-describedby="edit-form-key-name-feedback"
                            />
                            <b-form-invalid-feedback id="edit-form-key-name-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('값 타입')" mode="form" labelFor="edit-form-value-type" required>
                    <validation-provider
                        v-slot="validationContext"
                        name="edit-form-value-type"
                        class="w-100"
                        :rules="{ required: true }"
                    >
                        <b-form-select id="edit-form-value-type"
                                       v-model="form.value_type"
                                       :disabled="editLoading"
                                       autocomplete="new-password"
                                       :state="getValidationState(validationContext)"
                                       aria-describedby="edit-form-value-type-feedback"
                                       name="edit-form-value-type"
                                       :options="valueTypeOptions"

                        />
                        <b-form-invalid-feedback id="edit-form-value-type-feedback">
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </validation-provider>
                </DescriptionItem>

                <DescriptionItem :label="$t('라벨 키')" mode="form" labelFor="edit-form-label-key"
                                 required
                >
                    <div class="w-100 d-flex justify-content-between">
                        <div class="w-100">
                            <validation-provider
                                v-slot="validationContext"
                                name="edit-form-label-key"
                                :rules="{ required: true, min: 2 }"
                            >
                                <b-form-input id="edit-form-label-key"
                                              :value="labelInfo"
                                              name="edit-form-label-key"
                                              disabled
                                              :placeholder="$t('선택 버튼을 클릭하여 라벨 키를 선택해주세요.')"
                                              :state="getValidationState(validationContext)"
                                              autocomplete="new-password"
                                              aria-describedby="edit-form-label-key-feedback"
                                />
                                <b-form-invalid-feedback id="edit-form-label-key-feedback">
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </validation-provider>
                        </div>

                        <div class="ml-2">
                            <b-button :disabled="editLoading" @click="openLocaleStringSearchModal('form')">선택</b-button>
                        </div>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('필수 여부')" mode="form" labelFor="edit-form-is-required">
                    <validation-provider
                        v-slot="validationContext"
                        name="edit-form-is-required"
                    >
                        <b-form-radio-group
                            id="edit-form-is-required"
                            v-model="form.is_required"
                            :options="isRequiredOptions"
                            :button-variant="form.is_required ? 'outline-success' : 'outline-secondary'"
                            :state="getValidationState(validationContext)"
                            size="sm"
                            name="edit-form-is-required"
                            buttons
                            aria-describedby="edit-form-is-required-feedback"
                        />
                        <b-form-invalid-feedback id="edit-form-is-required-feedback"
                                                 class="d-block"
                                                 :class="{'mt-0': getValidationState(validationContext) !== false }"
                        >
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </validation-provider>
                </DescriptionItem>

                <DescriptionItem :label="$t('설명')" mode="form" labelFor="edit-form-description">
                    <b-form-textarea id="edit-form-description"
                                     v-model="form.description"
                                     :disabled="editLoading"
                                     :placeholder="descriptionPlaceHolder"
                                     autocomplete="new-password"
                    />
                </DescriptionItem>
                <template v-if="form.value_type === 'list'">
                    <DescriptionItem :label="$t('허용값 목록')" mode="form" labelFor="edit-form-allowed-values">
                        <div class="allowed-values">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <small class="text-muted">드래그하여 순서를 변경하고, 각 항목의 값과 라벨 키를 설정하세요.</small>
                                <b-button size="sm" variant="outline-secondary" :disabled="editLoading"
                                          @click="addAllowedValue">허용값 추가
                                </b-button>
                            </div>
                            <draggable
                                v-model="allowed_values"
                                handle=".drag-handle"
                                animation="200"
                                ghost-class="ghost-placeholder"
                                drag-class="dragging-item"
                                class="draggable-list"
                                :class="{'is-dragging': isDraggingAllowed}"
                                @start="isDraggingAllowed = true"
                                @end="isDraggingAllowed = false"
                            >
                                <div
                                    v-for="(item, idx) in allowed_values"
                                    :key="item.__uid || idx"
                                    class="draggable-item av-item"
                                >
                                    <div class="drag-handle">
                                        <fa icon="grip-vertical" class="mr-2 handle"/>
                                    </div>
                                    <div class="av-fields">
                                        <div class="av-field av-value">
                                            <validation-provider v-slot="validationContext"
                                                                 :name="`allowed-value-${idx}`"
                                                                 :vid="`allowed-value-${idx}`"
                                                                 :rules="{ required: true }">
                                                <b-form-input
                                                    :id="`allowed-value-${idx}`"
                                                    v-model="item.actual_value"
                                                    :state="getValidationState(validationContext)"
                                                    :placeholder="$t('값을 입력하세요')"
                                                    :disabled="editLoading || item.id != null"
                                                    autocomplete="new-password"
                                                />
                                                <small v-if="item.id != null" class="text-muted">
                                                    저장된 값은 규칙이 참조하므로 바꿀 수 없습니다 (라벨·순서만 수정 가능)
                                                </small>
                                                <b-form-invalid-feedback>{{
                                                        validationContext.errors[0]
                                                    }}
                                                </b-form-invalid-feedback>
                                            </validation-provider>
                                        </div>

                                        <div class="av-field av-label">
                                            <validation-provider v-slot="validationContext"
                                                                 :name="`allowed-label-key-${idx}`"
                                                                 :vid="`allowed-label-key-${idx}`"
                                                                 :rules="{ required: true }">
                                                <!-- 표시용 입력 -->
                                                <b-form-input :id="`allowed-label-key-${idx}`"
                                                              :value="item.label_key"
                                                              :name="`allowed-label-key-${idx}`"
                                                              disabled
                                                              :state="getValidationState(validationContext)"
                                                              :placeholder="$t('라벨 키를 선택하세요')"
                                                              autocomplete="new-password"
                                                              :aria-describedby="`allowed-label-key-${idx}-feedback`"
                                                />
                                                <b-form-invalid-feedback :vid="`allowed-label-key-${idx}-feedback`">
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </validation-provider>
                                            <b-button class="mt-1"
                                                      size="sm"
                                                      @click="openLocaleStringSearchModal('item', idx)">선택
                                            </b-button>
                                        </div>
                                        
                                        <div class="av-actions">
                                            <b-button class="mt-1" size="sm" variant="outline-danger"
                                                      :disabled="editLoading"
                                                      @click="removeAllowedValue(idx)">삭제
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </draggable>
                        </div>
                    </DescriptionItem>
                </template>


                <div v-if="mode === 'edit'" class="row-wrapper">
                    <DescriptionItem :label="$t('등록일시')">
                        {{ get_date(detailData.created_at) || '-' }}
                    </DescriptionItem>

                    <DescriptionItem :label="$t('수정일시')">
                        {{ get_date(detailData.updated_at) || '-' }}
                    </DescriptionItem>
                </div>
            </dl>
        </b-form>

        <LocaleStringSearchModal :selected-data="localeStringModalSelectedData"
                                 type="radio"
                                 @on-select="onSelectLocaleString"
        />
    </validation-observer>
</template>

<script>
import {createAllowedValues, createInputKey, deleteAllowedValue, deleteInputKey, updateAllowedValues, updateInputKey} from '@shared_utils/api/projectSchema';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import {countUpdatedAllowedValues} from '@shared/utils/countUpdatedAllowedValues';
import DescriptionItem from '@shared/components/common/DescriptionItem.vue';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import {
    isRequiredOptions,
    valueTypeOptions
} from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/data';
import LocaleStringSearchModal
    from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/LocaleStringSearchModal.vue';
import {getProjectCodeOptions} from '@shared/components/admin/pages/Management/common-data';
import draggable from 'vuedraggable';
import {getDate} from '@shared/utils/common';

export default {
    name: 'SchemaInputForm',
    components: {
        LocaleStringSearchModal,
        AutoCompleteSelect,
        DescriptionItem,
        draggable,
    },
    props: {
        detailData: {
            type: [Object, null],
            default: null,
        },
        mode: {
            type: String,
            required: true,
            default: 'add', // add | edit
        },
        projects: {
            type: [Array, null],
            default: null,
        },
        propAllowedValues: {
            type: Array,
            default: () => [],
        },
        propSelectedProjectCodeId: {
            type: [Number, String, null],
            default: null,
        },
        isCopy: {
            type: Boolean,
            default: false,
        },
        // 연결 프로젝트를 propSelectedProjectCodeId 로 고정한다(복사 모드 포함) + 셀렉터 잠금.
        // 배정 콘솔처럼 프로젝트가 이미 정해진 문맥에서 true. 고급 탭은 false(기존 동작).
        lockProjectToSelected: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            selectedProjectCodeId: null,
            selectedCampaignId: null,

            selectedLocaleString: [],
            activeLocaleTarget: null, // { type: 'form' | 'item', index?: number }
            isDraggingAllowed: false,
            editLoading: false,
            form: {
                key_name: null,
                value_type: 'int',
                description: null,
                is_required: false,
            },
            allowed_values: [],
            initialAllowedValues: [],
            savedSnapshot: null,
        };
    },
    computed: {
        // 부모 모달이 닫기 전 미저장 확인에 쓴다 (@dirty)
        dirty() {
            return this.savedSnapshot != null && this.snapshot() !== this.savedSnapshot;
        },
        get_date() {
            return (date) => {
                return getDate(date);
            };
        },
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        selectedLocaleStringKey() {
            return this.selectedLocaleString.map(item => item.key);
        },
        localeStringModalSelectedData() {
            if (this.activeLocaleTarget && this.activeLocaleTarget.type === 'item') {
                const idx = this.activeLocaleTarget.index;
                const key = this.allowed_values?.[idx]?.label_key;
                return key ? [key] : [];
            }
            return this.selectedLocaleStringKey;
        },
        descriptionPlaceHolder(){
            // eslint-disable-next-line quotes
            return `설명 (선택사항) - 프로젝트 입력 폼에서 가이드로 사용됩니다. 로케일을 적용하려면 다음 JSON 형식으로 작성해주세요(html 사용).\n {"ko": "한국어 설명", "en": "English description", "ca": "chinese description"}`;
        },
        isRequiredOptions() {
            return isRequiredOptions;
        },
        valueTypeOptions() {
            return valueTypeOptions;
        },
        labelInfo() {
            if (this.selectedLocaleString.length > 0) {
                return `${this.selectedLocaleString[0].key} ( ko: ${this.selectedLocaleString[0]?.ko ?? '-'}, en: ${this.selectedLocaleString[0]?.en ?? '-'}, ca: ${this.selectedLocaleString[0]?.ca ?? '-'} )`;
            }
            return null;
        }
    },
    watch: {
        // [순서 의존성 주의] Vue2 initWatch 는 watch 키를 선언 순서대로 워처로 생성한다.
        // 'detailData'(immediate) 가 'form.value_type' 보다 먼저 선언되어야, list 타입을
        // edit/복사로 fresh mount 할 때 시드된 allowed_values 가 'form.value_type' 워처에
        // 의해 비워지지 않는다(시드 시점엔 value_type 워처의 baseline 이 이미 list 라 미발화).
        // 이 선언 순서를 바꾸지 말 것.
        propSelectedProjectCodeId: {
            immediate: true,
            handler() {
                // 수정은 그 질문의 프로젝트(detailData.project_id)로 고정한다 — 열린 채 콘솔 선택이 바뀌어도 다른 프로젝트로 저장되지 않게
                if (this.mode === 'edit') return;
                // 콘솔(lockProjectToSelected)에서는 복사 모드에서도 현재 프로젝트를 유지한다.
                // 고급 탭 복사(lock 아님)에서만 비워 사용자가 대상 프로젝트를 고르게 한다.
                if (this.propSelectedProjectCodeId && (!this.isCopy || this.lockProjectToSelected)) {
                    this.selectedProjectCodeId = this.propSelectedProjectCodeId;
                } else {
                    this.selectedProjectCodeId = null;
                }
            }
        },
        detailData: {
            immediate: true,
            handler() {
                this.form = {...this.form, ...this.detailData};
                if ((this.mode === 'edit' || this.isCopy) && this.detailData) {
                    this.selectedLocaleString = [{
                        key: this.detailData.label_key,
                        ...this.detailData.label_texts
                    }];

                    this.allowed_values = JSON.parse(JSON.stringify(this.propAllowedValues || []));
                    this.initialAllowedValues = JSON.parse(JSON.stringify(this.propAllowedValues || []));

                    if (this.mode === 'edit') {
                        this.selectedProjectCodeId = this.detailData.project_id;
                    }
                    // isCopy: selectedProjectCodeId 는 null 유지 (연결 프로젝트 비움)
                }
                // 열 때(복사·콘솔이 채운 키 이름 포함)의 값을 미저장 판정 기준으로 삼는다
                this.savedSnapshot = this.snapshot();
            }
        },
        'form.value_type'(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (oldVal && this.allowed_values.length > 0) {
                    this.$EventBus.$emit('toast-warning', this.$t('값 타입 변경'), '값 타입을 바꿔 입력한 허용값 목록을 비웠습니다.');
                }
                this.allowed_values = [];
                this.initialAllowedValues = [];
            }
        },
        dirty(value) {
            this.$emit('dirty', value);
        },
    },
    beforeDestroy() {
        this.$emit('dirty', false);
    },
    methods: {
        snapshot() {
            const {key_name, value_type, description, is_required} = this.form;
            return JSON.stringify([
                key_name ?? '', value_type, description ?? '', is_required, this.selectedProjectCodeId, this.selectedLocaleStringKey,
                this.allowed_values.map((v) => [v.actual_value ?? '', v.label_key ?? '']),
            ]);
        },
        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        reloadFromProps(values) {
            // 인자가 주어지면 그 값을 baseline 으로 사용한다.
            // parent 가 fetch 직후 child prop 반영을 기다리지 않고 호출하는 경우
            // (propAllowedValues 가 stale 일 수 있음) 의 타이밍 의존을 제거하기 위함.
            const source = values !== undefined ? values : this.propAllowedValues;
            this.allowed_values = JSON.parse(JSON.stringify(source || []));
            this.initialAllowedValues = JSON.parse(JSON.stringify(source || []));
        },
        addAllowedValue() {
            this.allowed_values.push({
                __uid: `av-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                actual_value: '',
                label_key: '',
            });
        },
        removeAllowedValue(index) {
            this.allowed_values.splice(index, 1);
        },
        // 수정 저장 시 허용값이 지워지면 대상·건수를 확인받는다. 지울 값이 없으면 바로 저장한다.
        confirmAndSave() {
            if (this.editLoading) return;
            const removed = this.mode === 'edit' && this.form.value_type === 'list'
                ? this.initialAllowedValues.filter(v => v.id != null && !this.allowed_values.some(av => av.id === v.id))
                : [];
            if (removed.length === 0) return this.saveEditData();
            const added = this.allowed_values.filter(v => v.id == null).length;
            const updated = countUpdatedAllowedValues(this.initialAllowedValues, this.allowed_values);
            const h = this.$createElement;
            return this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '허용값 삭제 확인',
                message: h('div', {style: {whiteSpace: 'pre-line'}},
                    `허용값 삭제 ${removed.length}건: ${removed.map(v => `${v.actual_value}(${v.label_key})`).join(', ')}
추가 ${added}건${updated ? ` / 수정 ${updated}건` : ''}
배정 규칙이 쓰는 값은 삭제되지 않고 오류로 표시됩니다. 삭제한 값은 되돌릴 수 없습니다.`)
            }).then((ok) => ok && this.saveEditData());
        },
        async saveEditData() {
            try {
                this.errData = null;
                this.editLoading = true;

                let response = null;
                const form = {
                    ...this.form,
                    project_id: this.selectedProjectCodeId,
                    label_key: this.selectedLocaleStringKey?.[0],
                };
                // sort_ord 계산 및 payload 정리
                
                if (this.mode === 'add') {
                    response = await createInputKey(form);

                    if (this.form.value_type === 'list') {
                        const form = {
                            input_key_id: response.data.id,
                            items: this.allowed_values.map((v, idx) => ({
                                actual_value: v.actual_value,
                                label_key: v.label_key,
                                sort_order: idx
                            }))
                        };
                        try {
                            await createAllowedValues(form);
                        } catch (allowedError) {
                            allowedError.keyCreated = true;
                            // 허용값 없는 선택지 질문이 남지 않도록 방금 만든 키를 되돌린다
                            try {
                                await deleteInputKey(response.data.id);
                                allowedError.compensated = true;
                            } catch (rollbackError) {
                                console.error(rollbackError);
                            }
                            throw allowedError;
                        }
                    }
                } else if (this.mode === 'edit') {
                    response = await updateInputKey(this.detailData.id, form);

                    if (this.form.value_type === 'list') {
                        const updateForm = {
                            input_key_id: response.data.id,
                            items: []
                        };
                        const addForm = {
                            input_key_id: response.data.id,
                            items: []
                        };
                        const deleteForm = {
                            input_key_id: response.data.id,
                            items: []
                        };
                        
                        this.allowed_values.forEach((v, idx) =>{
                            if(v.id != null) {
                                updateForm.items.push({
                                    actual_value: v.actual_value,
                                    label_key: v.label_key,
                                    sort_order: idx,
                                    id: v.id,
                                });
                            } else {
                                addForm.items.push({
                                    actual_value: v.actual_value,
                                    label_key: v.label_key,
                                    sort_order: idx,
                                });
                            }
                        });
                        this.initialAllowedValues.forEach((v) => {
                            if (v.id != null && !this.allowed_values.some(av => av.id === v.id)) {
                                deleteForm.items.push({
                                    id: v.id,
                                });
                            }
                        });

                        // DELETE 는 시퀀스의 마지막에 실행한다.
                        // POST/PUT 이 먼저 실패하면 DELETE 가 발생하지 않아 기존 데이터가 보존된다.
                        if(addForm.items.length > 0) {
                            await createAllowedValues(addForm);
                        }
                        if(updateForm.items.length > 0) {
                            await updateAllowedValues(updateForm);
                        }
                        if(deleteForm.items.length > 0) {
                            await Promise.all(
                                deleteForm.items.map(item => deleteAllowedValue(item.id))
                            );
                        }

                    }
                }

                this.$EventBus.$emit('toast-success', this.$t('스키마 입력 값 등록 및 수정'), this.$t('스키마 입력 값이 성공적으로 저장되었습니다.'));
                if (response && response.data) {
                    this.$emit('updateDetailData', {...this.detailData, ...this.form, ...response.data});
                } else {
                    this.$emit('updateDetailData', {...this.detailData, ...this.form});
                }
            } catch (error) {
                console.error(error);
                let failureMessage = getErrorMessage(error, '스키마 입력 값 데이터 저장 도중 오류가 발생했습니다.');
                if (this.mode === 'add' && this.form.value_type === 'list' && error.keyCreated) {
                    failureMessage = error.compensated
                        ? `허용값 저장에 실패해 키 생성도 취소했습니다. 다시 시도해주세요. (${failureMessage})`
                        : `키 생성 후 허용값 저장에 실패했습니다. 목록에서 해당 키를 찾아 허용값을 다시 저장해주세요. (${failureMessage})`;
                }
                this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 등록 및 수정'), failureMessage);
                if (this.mode === 'edit' && this.form.value_type === 'list') {
                    // editLoading 을 유지한 채 parent 의 refresh 완료를 기다린다.
                    // refresh 가 끝나기 전에 사용자가 재시도하면 stale state 로 다시 DELETE 가 발생할 수 있다.
                    await new Promise((resolve) => {
                        let settled = false;
                        const done = () => {
                            if (settled) return;
                            settled = true;
                            resolve();
                        };
                        this.$emit('refreshAllowedValues', done);
                        // 부모가 핸들러를 등록하지 않았거나 done 을 호출하지 않는 경우에 대비한 safety net.
                        setTimeout(done, 5000);
                    });
                }
            } finally {
                this.editLoading = false;
            }
        },
        resetForm() {
            this.form = {
                key_name: null,
                value_type: 'int',
                description: null,
                is_required: false,
                allowed_values: [],
            };
            this.selectedProjectCodeId = null;
            this.selectedLocaleString = [];
            this.activeLocaleTarget = null;
        },
        onSelectLocaleString(localeString) {
            const arr = Object.values(localeString || {});
            const selected = arr[0];
            if (this.activeLocaleTarget && this.activeLocaleTarget.type === 'item') {
                const idx = this.activeLocaleTarget.index;
                if (this.allowed_values[idx]) {
                    this.$set(this.allowed_values[idx], 'label_key', selected?.key || '');
                    // 전체 텍스트를 보관해 표시용으로 활용
                    const texts = selected ? {...selected} : null;
                    if (texts && Object.prototype.hasOwnProperty.call(texts, 'key')) delete texts.key;
                    this.$set(this.allowed_values[idx], 'label_texts', texts || null);
                }
            } else {
                this.selectedLocaleString = arr;
            }
            this.activeLocaleTarget = null;
        },
        openLocaleStringSearchModal(type = 'form', index = null) {
            this.activeLocaleTarget = {type, index};
            this.$bvModal.show('locale-string-search-modal');
        },
    },
};
</script>

<style scoped lang="scss">
.allowed-values {
    width: 100%;

    .draggable-list {
        width: 100%;
    }

    .av-item {
        display: flex;
        align-items: center;
        padding: 0.5rem;
        margin-bottom: 0.5rem;
        background: #fff;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        width: 100%; // 가로 전체 사용
        .drag-handle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            margin-right: 0.5rem;
            color: #6c757d;
            cursor: grab;

            &:active {
                cursor: grabbing;
            }
        }

        .av-fields {
            display: flex;
            gap: 8px;
            flex: 1;
            width: 100%; // 내부 필드 래퍼도 전체 사용
            .av-field {
                display: flex;
                align-items: center;
            }

            .av-value {
                flex: 0 0 240px;
                min-width: 0;
            }

            .av-value .form-control {
                width: 100%;
            }

            .av-label {
                flex: 1;
                display: grid;
                grid-template-columns: 1fr auto;
                align-items: start;
                align-content: start;
                gap: 8px;
                min-width: 0;
            }

            .av-label .btn {
                align-self: start;
            }

            .av-label .form-control {
                width: 100%;
            }

            .av-actions {
                margin-left: 8px;
                flex: 0 0 auto;
            }
        }
    }
}
</style>
